import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from 'shared/api/client';
import type { paths } from 'shared/api/generated/api';
import { usePermissions } from 'app/api/permission-queries';

type LockResponse = paths['/locks/{documentSuperId}/{sectionId}']['post']['responses'][200]['content']['application/json'];
type LockInfo = Partial<LockResponse>;

const LOCK_RENEW_MARGIN_MS = 60000;
const LOCK_RENEW_MIN_MS = 15000;

const useLock = (documentId?: string, sectionId?: string, enabled = true, { taggingLock = false }: { taggingLock?: boolean } = {}) => {
  const [lockInfo, setLockInfo] = useState<LockInfo | false>(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sectionPermissions } = usePermissions(documentId as string);
  const hasWritePermission = sectionPermissions?.[sectionId ?? ''] === 'WRITE' || taggingLock;
  const shouldLock = hasWritePermission && enabled;
  const isMounted = useRef(true);

  const getLock = useCallback(async () => {
    try {
      const { data: lock } = await api.POST('/locks/{documentSuperId}/{sectionId}', {
        params: { path: { documentSuperId: Number(documentId), sectionId: sectionId as string } },
      });
      if (isMounted.current) {
        if (lock) {
          setLockInfo(lock);
        }
      }
      return lock;
    } catch (message) {
      console.error(message);
      return undefined;
    }
  }, [documentId, sectionId]);

  const deleteLock = useCallback(async () => {
    try {
      const res = await api.DELETE('/locks/{documentSuperId}/{sectionId}', {
        params: { path: { documentSuperId: Number(documentId), sectionId: sectionId as string } },
      });
      if (isMounted.current) {
        setLockInfo({});
      }
      return res;
    } catch (message) {
      return console.error(message);
    }
  }, [documentId, sectionId]);

  useEffect(() => {
    isMounted.current = true;
    const scheduleRenewal = (lock: LockInfo | undefined) => {
      if (!lock?.isValidForUser || !lock?.isValidNow || !isMounted.current) {
        return;
      }
      const untilExpiry = new Date(lock.validUntil ?? NaN).getTime() - Date.now();
      if (Number.isNaN(untilExpiry)) {
        return;
      }
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        if (isMounted.current) {
          getLock().then(scheduleRenewal);
        }
      }, Math.max(untilExpiry - LOCK_RENEW_MARGIN_MS, LOCK_RENEW_MIN_MS));
    };
    if (shouldLock) {
      getLock().then(scheduleRenewal);
    }

    return () => {
      isMounted.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
      }
      if (shouldLock) {
        deleteLock();
      }
      timer.current = null;
    };
  }, [sectionId, documentId, shouldLock, getLock, deleteLock]);
  return {
    hasLock: shouldLock && !!(lockInfo && lockInfo.isValidForUser) && !!(lockInfo && lockInfo.isValidNow),
    lockInfo: lockInfo as LockInfo,
    deleteLock,
  };
};

export default useLock;
